import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import axios, { AxiosResponse } from "axios";
import _ from "lodash";
import { ToastWarning } from "../../../../Shared/Toasts/ToastWarning";

/**
 * Get articles with movement summary (NEW API)
 * @public
 */
export const findArticleMovementSummaryApi = createAsyncThunk(
  "mouvement/findArticleMovementSummaryApi",
  async (params: any, { rejectWithValue }) => {
    try {
      const {
        page = 1,
        limit = 5,
        startDate,
        endDate,
        search,
        societe_id,
        searchElement,
        organisation_id,
        refFrom,
        refTo,
        selectedSousCategorie,
        selectedMarque,
        sortBy,
      } = params;

      const queryParams = new URLSearchParams();
      queryParams.append("page", page.toString());
      queryParams.append("limit", limit.toString());

      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (search) queryParams.append("search", search);
      if (societe_id) queryParams.append("societe_id", societe_id);
      if (organisation_id)
        queryParams.append("organisation_id", organisation_id);
      if (searchElement) queryParams.append("searchElement", searchElement);
      if (refFrom) queryParams.append("referenceFrom", refFrom);
      if (refTo) queryParams.append("referenceTo", refTo);
      if (selectedSousCategorie)
        queryParams.append("selectedSousCategorie", selectedSousCategorie);
      if (selectedMarque) queryParams.append("selectedMarque", selectedMarque);
      if (sortBy) queryParams.append("sortBy", sortBy);

      const response = await axios.get(
        `mouvement/summary?${queryParams.toString()}`,
      );

      if (response.data.success) {
        return response.data;
      } else {
        ToastWarning(
          response.data.message || "Erreur lors de la récupération des données",
        );
        return rejectWithValue({
          success: false,
          message: response.data.message,
        });
      }
    } catch (error: any) {
      console.error("Error in findArticleMovementSummaryApi:", error);
      const errorMessage = error.response?.data?.message || "Erreur serveur";
      ToastWarning(errorMessage);
      return rejectWithValue({
        success: false,
        message: errorMessage,
      });
    }
  },
);

/**
 * Get detailed movements for a specific article (NEW API)
 * @public
 */
export const findArticleMovementDetailsApi = createAsyncThunk(
  "mouvement/findArticleMovementDetailsApi",
  async (params: any, { rejectWithValue }) => {
    try {
      const { articleId, startDate, endDate } = params;

      if (!articleId) {
        ToastWarning("ID de l'article requis");
        return rejectWithValue({
          success: false,
          message: "ID de l'article requis",
        });
      }

      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);

      const queryString = queryParams.toString();
      const url = `mouvement/details/${articleId}${
        queryString ? `?${queryString}` : ""
      }`;

      const response = await axios.get(url);

      if (response.data.success) {
        return response.data;
      } else {
        ToastWarning(
          response.data.message || "Erreur lors de la récupération des détails",
        );
        return rejectWithValue({
          success: false,
          message: response.data.message,
        });
      }
    } catch (error: any) {
      console.error("Error in findArticleMovementDetailsApi:", error);
      const errorMessage = error.response?.data?.message || "Erreur serveur";
      ToastWarning(errorMessage);
      return rejectWithValue({
        success: false,
        message: errorMessage,
      });
    }
  },
);

/**
 * Export all articles with movement summary to Excel (NEW API)
 * @public
 */
export const exportArticlesMovementSummaryApi = createAsyncThunk(
  "mouvement/exportArticlesMovementSummaryApi",
  async (params: any, { rejectWithValue }) => {
    try {
      const { startDate, endDate, search, societe_id } = params;

      const queryParams = new URLSearchParams();
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (search) queryParams.append("search", search);
      if (societe_id) queryParams.append("societe_id", societe_id);

      const response = await axios.get(
        `mouvement/export?${queryParams.toString()}`,
        {
          responseType: "blob",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
        },
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const date = new Date().toISOString().split("T")[0];
      const fileName = `export-mouvements-${date}.xlsx`;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error: any) {
      console.error("Error in exportArticlesMovementSummaryApi:", error);
      const errorMessage =
        error.response?.data?.message || "Erreur lors de l'export";
      ToastWarning(errorMessage);
      return rejectWithValue({
        success: false,
        message: errorMessage,
      });
    }
  },
);

/**
 * Get sales by famille (souscategorie) — today / this month / this year
 * @public
 */
export const findSalesByFamilleApi = createAsyncThunk(
  "mouvement/findSalesByFamilleApi",
  async (params: any, { rejectWithValue }) => {
    try {
      const { societe_id, organisation_id } = params;

      const queryParams = new URLSearchParams();
      if (societe_id) queryParams.append("societe_id", societe_id);
      if (organisation_id)
        queryParams.append("organisation_id", organisation_id);

      const response = await axios.get(
        `mouvement/getSalesByFamilleAPI?${queryParams.toString()}`,
      );

      if (response.data.success) {
        return response.data;
      } else {
        ToastWarning(
          response.data.message || "Erreur lors de la récupération des données",
        );
        return rejectWithValue({
          success: false,
          message: response.data.message,
        });
      }
    } catch (error: any) {
      console.error("Error in findSalesByFamilleApi:", error);
      const errorMessage = error.response?.data?.message || "Erreur serveur";
      ToastWarning(errorMessage);
      return rejectWithValue({
        success: false,
        message: errorMessage,
      });
    }
  },
);

/**
 * Get list of Mouvement of article (OLD API - keep for compatibility)
 * @public
 */
export const mouvementArticleApi = createAsyncThunk(
  "mouvement/mouvementArticleApi",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(`mouvement/mouvementArticleApi/`, data);
      if (response.data.data !== null) {
        return response.data;
      } else {
        ToastWarning(response.data.message);
        return rejectWithValue({
          success: false,
          message: response.data.message,
        });
      }
    } catch (error: any) {
      console.log(error);
      return rejectWithValue({
        success: false,
        message: error.response?.data?.message || "Erreur serveur",
      });
    }
  },
);

/**
 * Get list of Mouvement Vente of article (OLD API)
 * @public
 */
export const mouvementArticleVenteApi = createAsyncThunk(
  "mouvement/mouvementArticleVenteApi",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `mouvement/mouvementArticleVenteApi/`,
        data,
      );
      if (response.data.data !== null) {
        return response.data;
      } else {
        ToastWarning(response.data.message);
        return rejectWithValue({
          success: false,
          message: response.data.message,
        });
      }
    } catch (error: any) {
      console.log(error);
      return rejectWithValue({
        success: false,
        message: error.response?.data?.message || "Erreur serveur",
      });
    }
  },
);

/**
 * Get list of Mouvement of article (OLD API)
 * @public
 */
export const mouvementVenteArticleApi = createAsyncThunk(
  "mouvement/mouvementVenteArticleApi",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `mouvement/mouvementVenteArticleApi/`,
        data,
      );
      if (response.data.data !== null) {
        return response.data;
      } else {
        ToastWarning(response.data.message);
        return rejectWithValue({
          success: false,
          message: response.data.message,
        });
      }
    } catch (error: any) {
      console.log(error);
      return rejectWithValue({
        success: false,
        message: error.response?.data?.message || "Erreur serveur",
      });
    }
  },
);

/**
 * Get specific action (OLD API)
 * @public
 */
export const findActionApi = createAsyncThunk(
  "mouvement/findActionApi",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(`mouvement/findActionApi/${id}`);
      if (response.data.data !== null) {
        return response.data;
      } else {
        ToastWarning(response.data.message);
        return rejectWithValue({
          success: false,
          message: response.data.message,
        });
      }
    } catch (error: any) {
      console.log(error);
      return rejectWithValue({
        success: false,
        message: error.response?.data?.message || "Erreur serveur",
      });
    }
  },
);

interface MouvementState {
  listMouvement: any[];
  listMouvementCaisse: any[];
  articlesWithSummary: any[];
  movementDetails: any[];
  salesByFamille: { today: any[]; month: any[]; year: any[] } | null;
  totalPages: any;
  currentpage: any;
  totaux: any;
  count: any;
  total: any;
  loading: boolean;
  salesByFamilleLoading: boolean;
  error: string | null;
  exporting: boolean;
}

const initialState: MouvementState = {
  listMouvementCaisse: [],
  listMouvement: [],
  articlesWithSummary: [],
  movementDetails: [],
  salesByFamille: null,
  totalPages: null,
  currentpage: null,
  totaux: null,
  count: null,
  total: null,
  loading: false,
  salesByFamilleLoading: false,
  error: null,
  exporting: false,
};

export const MouvementSlice = createSlice({
  name: "mouvement",
  initialState,
  reducers: {
    resetSummary: (state) => {
      state.articlesWithSummary = [];
      state.totalPages = null;
      state.currentpage = null;
      state.loading = false;
      state.error = null;
    },
    resetDetails: (state) => {
      state.movementDetails = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // findArticleMovementSummaryApi
    builder.addCase(findArticleMovementSummaryApi.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      findArticleMovementSummaryApi.fulfilled,
      (state, action) => {
        state.loading = false;
        state.articlesWithSummary = action.payload.data || [];
        state.totalPages = action.payload.pagination?.totalPages || 1;
        state.currentpage = action.payload.pagination?.page || 1;
        state.total = action.payload.pagination?.totalItems || 0;
      },
    );
    builder.addCase(findArticleMovementSummaryApi.rejected, (state) => {
      state.loading = false;
      state.error = "Erreur inconnue";
      state.articlesWithSummary = [];
    });

    // findArticleMovementDetailsApi
    builder.addCase(findArticleMovementDetailsApi.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      findArticleMovementDetailsApi.fulfilled,
      (state, action) => {
        state.loading = false;
        state.movementDetails = action.payload.data || [];
      },
    );
    builder.addCase(findArticleMovementDetailsApi.rejected, (state) => {
      state.loading = false;
      state.error = "Erreur inconnue";
      state.movementDetails = [];
    });

    // findSalesByFamilleApi
    builder.addCase(findSalesByFamilleApi.pending, (state) => {
      state.salesByFamilleLoading = true;
      state.error = null;
    });
    builder.addCase(findSalesByFamilleApi.fulfilled, (state, action) => {
      state.salesByFamilleLoading = false;
      state.salesByFamille = action.payload.data || null;
    });
    builder.addCase(findSalesByFamilleApi.rejected, (state) => {
      state.salesByFamilleLoading = false;
      state.error = "Erreur inconnue";
      state.salesByFamille = null;
    });

    // OLD APIs
    builder.addCase(mouvementArticleApi.fulfilled, (state, action) => {
      state.listMouvement = action.payload.data?.data || [];
      state.totalPages = action.payload.data?.totalPages || null;
      state.currentpage = action.payload.data?.currentPage || null;
    });
    builder.addCase(mouvementArticleVenteApi.fulfilled, (state, action) => {
      state.listMouvement = action.payload.data?.data || [];
      state.totalPages = action.payload.data?.totalPages || null;
      state.currentpage = action.payload.data?.currentPage || null;
      state.total = action.payload.data?.total || null;
    });
    builder.addCase(mouvementVenteArticleApi.fulfilled, (state, action) => {
      if (!action.payload.data?.exportdata) {
        state.listMouvementCaisse = action.payload.data?.list || [];
        state.totalPages = action.payload.data?.totalPages || null;
        state.currentpage = action.payload.data?.currentPage || null;
        state.totaux = action.payload.data?.totaux || null;
        state.count = action.payload.data?.count || null;
      }
    });
    builder.addCase(exportArticlesMovementSummaryApi.rejected, (state) => {
      state.exporting = false;
      state.error = "Erreur lors de l'export";
    });
    builder.addCase(findActionApi.fulfilled, (state, action) => {
      // Handle as needed
    });
  },
});

// Export actions
export const { resetSummary, resetDetails } = MouvementSlice.actions;

// Export selectors
export const currentpage = (state: RootState) => state.mouvement.currentpage;
export const totalPages = (state: RootState) => state.mouvement.totalPages;
export const ListMouvement = (state: RootState) =>
  state.mouvement.listMouvement;
export const ListMouvementCaisse = (state: RootState) =>
  state.mouvement.listMouvementCaisse;
export const Totaux = (state: RootState) => state.mouvement.totaux;
export const Count = (state: RootState) => state.mouvement.count;
export const Total = (state: RootState) => state.mouvement.total;

// NEW selectors
export const ArticlesWithSummary = (state: RootState) =>
  state.mouvement.articlesWithSummary;
export const MovementDetails = (state: RootState) =>
  state.mouvement.movementDetails;
export const SalesByFamille = (state: RootState) =>
  state.mouvement.salesByFamille;
export const Loading = (state: RootState) => state.mouvement.loading;
export const SalesByFamilleLoading = (state: RootState) =>
  state.mouvement.salesByFamilleLoading;
export const Error = (state: RootState) => state.mouvement.error;
export const Exporting = (state: RootState) => state.mouvement.exporting;

export default MouvementSlice.reducer;